/**
 * Benefit Policy v1 — precedence and stacking for the Access & Benefits Engine.
 *
 * PRECEDENCE
 *   Benefits resolve into pricing layers, and Pricing Policy v2 decides the
 *   winner by layer:
 *     campaign → scholarship → coupon → partner → institution → regional
 *   Within one layer, the benefit `priority` from `benefit-config.ts` breaks the
 *   tie first, then the larger saving, then code order. Deterministic always.
 *
 * STACKING
 *   Disabled. Exactly ONE benefit applies to an order. Losers are recorded with
 *   a reason so every decision is auditable rather than silent.
 *
 * ENTITLEMENTS BEAT CODES
 *   When an account-based entitlement and a typed code resolve to the SAME
 *   benefit id, the entitlement wins — that is the migration path away from
 *   shared codes, and it means a person never loses their granted price by
 *   forgetting a code.
 */

import type { DiscountLayer } from "../pricing-types";
import { benefitRule } from "./benefit-config";
import type { BenefitCandidate, BenefitType } from "./benefit-types";

/** Pricing layer a benefit type discounts through. */
export function benefitLayer(type: BenefitType): DiscountLayer {
  return benefitRule(type).layer;
}

export const BENEFIT_STACKING_ENABLED = false;

export const BENEFIT_POLICY_SUMMARY = [
  "One benefit per order — stacking is disabled.",
  "Benefits resolve into pricing layers; Pricing Policy v2 picks the winner.",
  "An account entitlement beats a typed code for the same benefit.",
  "Every discount is taken off the region MRP, never off a discounted price.",
  "Rejected and superseded benefits are written to the audit log with a reason.",
];

const LAYER_ORDER: DiscountLayer[] = [
  "campaign",
  "scholarship",
  "coupon",
  "partner",
  "institution",
  "regional",
];

function layerRank(layer: DiscountLayer): number {
  const i = LAYER_ORDER.indexOf(layer);
  return i === -1 ? LAYER_ORDER.length : i;
}

const SOURCE_RANK: Record<BenefitCandidate["source"], number> = {
  grant: 0,
  invitation: 1,
  campaign: 2,
  coupon: 3,
};

/**
 * Order candidates so index 0 is the one that should apply. Pure and stable:
 * the same input always produces the same order.
 */
export function rankBenefits(candidates: BenefitCandidate[]): BenefitCandidate[] {
  return [...candidates].sort((a, b) => {
    const byLayer = layerRank(a.layer) - layerRank(b.layer);
    if (byLayer !== 0) return byLayer;
    const byPriority = a.priority - b.priority;
    if (byPriority !== 0) return byPriority;
    const bySource = SOURCE_RANK[a.source] - SOURCE_RANK[b.source];
    if (bySource !== 0) return bySource;
    return a.benefitId < b.benefitId ? -1 : a.benefitId > b.benefitId ? 1 : 0;
  });
}

/**
 * Drop a typed code when an entitlement already grants the same benefit id.
 * Returns the kept candidates plus the reason each removal happened.
 */
export function dedupeAgainstEntitlements(candidates: BenefitCandidate[]): {
  kept: BenefitCandidate[];
  skipped: Array<{ candidate: BenefitCandidate; reason: string }>;
} {
  const grantedIds = new Set(
    candidates.filter((c) => c.source === "grant").map((c) => c.benefitId),
  );
  const kept: BenefitCandidate[] = [];
  const skipped: Array<{ candidate: BenefitCandidate; reason: string }> = [];

  for (const c of candidates) {
    if (c.source !== "grant" && grantedIds.has(c.benefitId)) {
      skipped.push({
        candidate: c,
        reason: `already granted on this account as an entitlement (${c.benefitId})`,
      });
      continue;
    }
    kept.push(c);
  }
  return { kept, skipped };
}

/** Reason text for a candidate that lost to `winner`. */
export function supersededReason(
  loser: BenefitCandidate,
  winner: BenefitCandidate,
): string {
  return loser.layer === winner.layer
    ? `superseded by ${winner.benefitId} (same layer, higher benefit priority)`
    : `superseded by higher-precedence ${winner.layer} benefit ${winner.benefitId}`;
}
